import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Bot, Save, Clock, RotateCcw, AlertCircle, Play, Square, RefreshCw, WifiOff, RotateCw, FileText, Link, Copy, Check } from 'lucide-react'
import { useConfigStore } from '../store/configStore'
import { apiFetch } from '../lib/api'
import Button from './ui/Button'
import toast from 'react-hot-toast'

// ── Types ─────────────────────────────────────────────────────────────────────
type BotState = 'started' | 'stopped' | 'starting' | 'stopping' | 'unknown' | 'created' | 'replacing' | 'destroying' | 'destroyed'

interface BotStatus {
  configured: boolean
  state: BotState
  id?: string
  updated_at?: string
  error?: string
}

// ── Constants ─────────────────────────────────────────────────────────────────
const TIMEFRAMES = [
  { value: '1m',  label: '1 นาที' },
  { value: '5m',  label: '5 นาที' },
  { value: '15m', label: '15 นาที' },
  { value: '30m', label: '30 นาที' },
  { value: '60m', label: '60 นาที' },
  { value: '1h',  label: '1 ชั่วโมง' },
  { value: '1d',  label: 'รายวัน' },
  { value: '1wk', label: 'รายสัปดาห์' },
]

const STATE_META: Record<BotState, { label: string; dot: string; text: string; pulse: boolean }> = {
  started:    { label: 'Running',     dot: 'bg-emerald-400', text: 'text-gain', pulse: true },
  stopped:    { label: 'Stopped',     dot: 'bg-gray-400',    text: 'text-gray-500 dark:text-gray-400',       pulse: false },
  starting:   { label: 'Starting…',   dot: 'bg-yellow-400',  text: 'text-yellow-600 dark:text-yellow-400',   pulse: true },
  stopping:   { label: 'Stopping…',   dot: 'bg-orange-400',  text: 'text-orange-600 dark:text-orange-400',   pulse: true },
  unknown:    { label: 'Unknown',     dot: 'bg-gray-300',    text: 'text-gray-400',                          pulse: false },
  created:    { label: 'Created',     dot: 'bg-blue-400',    text: 'text-blue-500 dark:text-blue-400',       pulse: false },
  replacing:  { label: 'Replacing…',  dot: 'bg-purple-400',  text: 'text-purple-500 dark:text-purple-400',   pulse: true },
  destroying: { label: 'Destroying…', dot: 'bg-red-400',     text: 'text-loss',         pulse: true },
  destroyed:  { label: 'Destroyed',   dot: 'bg-red-300',     text: 'text-red-400 dark:text-red-300',         pulse: false },
}

const HR_OPTS  = Array.from({ length: 24 }, (_, i) => String(i))
const MIN_OPTS = Array.from({ length: 60 }, (_, i) => String(i))

// ── Sub-components ────────────────────────────────────────────────────────────
interface TimePickerProps {
  hour: string; minute: string
  onHourChange: (v: string) => void
  onMinuteChange: (v: string) => void
}

const TimePicker: React.FC<TimePickerProps> = ({ hour, minute, onHourChange, onMinuteChange }) => (
  <div className="flex items-center gap-1">
    <select value={hour} onChange={e => onHourChange(e.target.value)}
      className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-16 text-center">
      {HR_OPTS.map(h => <option key={h} value={h}>{h.padStart(2, '0')}</option>)}
    </select>
    <span className="text-gray-500 dark:text-gray-400 font-bold">:</span>
    <select value={minute} onChange={e => onMinuteChange(e.target.value)}
      className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-16 text-center">
      {MIN_OPTS.map(m => <option key={m} value={m}>{m.padStart(2, '0')}</option>)}
    </select>
    <span className="text-xs text-gray-400 ml-1">น.</span>
  </div>
)

// ── Telegram Link Card ────────────────────────────────────────────────────────
const TelegramLinkCard: React.FC = () => {
  const [token, setToken]     = useState<string | null>(null)
  const [expiresAt, setExpiresAt] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied]   = useState(false)
  const [secondsLeft, setSecondsLeft] = useState(0)

  useEffect(() => {
    if (!expiresAt) return
    const tick = setInterval(() => {
      const left = Math.max(0, Math.round((expiresAt - Date.now()) / 1000))
      setSecondsLeft(left)
      if (left === 0) { setToken(null); setExpiresAt(null) }
    }, 1000)
    return () => clearInterval(tick)
  }, [expiresAt])

  const generate = async () => {
    setLoading(true)
    try {
      const res = await apiFetch('/api/auth/telegram-link-token', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setToken(data.token)
      setExpiresAt(Date.now() + data.expires_in_seconds * 1000)
      setSecondsLeft(data.expires_in_seconds)
    } catch (err: any) {
      toast.error(err.message || 'สร้าง code ไม่สำเร็จ')
    } finally {
      setLoading(false)
    }
  }

  const copy = () => {
    if (!token) return
    navigator.clipboard.writeText(`/link ${token}`)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  const mins = Math.floor(secondsLeft / 60)
  const secs = String(secondsLeft % 60).padStart(2, '0')

  return (
    <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
      <div className="flex items-center gap-2 mb-3">
        <Link className="w-4 h-4 text-primary-500" />
        <span className="text-sm font-semibold text-gray-900 dark:text-white">เชื่อมต่อ Telegram</span>
        <span className="text-xs text-gray-400">สร้าง code แล้วส่งให้ bot</span>
      </div>

      {!token ? (
        <Button size="sm"
          onClick={generate}
          disabled={loading}
        >
          {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Link className="w-3.5 h-3.5" />}
          {loading ? 'กำลังสร้าง…' : 'สร้าง Link Code'}
        </Button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 text-xs font-mono font-bold text-primary-600 dark:text-primary-400 tracking-wider text-center break-all">
              {token}
            </code>
            <button
              onClick={copy}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              title="คัดลอกคำสั่ง"
            >
              {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            ส่งคำสั่งใน Telegram:{' '}
            <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">/link {token}</code>
          </p>
          <div className="flex items-center justify-between">
            <span className={`text-xs font-mono ${secondsLeft < 60 ? 'text-loss' : 'text-gray-400'}`}>
              หมดอายุใน {mins}:{secs}
            </span>
            <button
              onClick={generate}
              disabled={loading}
              className="text-xs text-primary-500 hover:text-primary-600 disabled:opacity-50"
            >
              สร้างใหม่
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
const BotSettingsSection: React.FC = () => {
  const { getConfigValue, updateConfig, fetchConfigs } = useConfigStore()

  // Schedule state
  const [dailyH,   setDailyH]   = useState('8')
  const [dailyM,   setDailyM]   = useState('0')
  const [alphaH,   setAlphaH]   = useState('9')
  const [alphaM,   setAlphaM]   = useState('0')
  const [finvizH,  setFinvizH]  = useState('7')
  const [finvizM,  setFinvizM]  = useState('0')
  const [timeframe, setTimeframe] = useState('1d')
  const [saving, setSaving] = useState<string | null>(null)

  // Report template toggles
  const [showDayPct, setShowDayPct] = useState(true)
  const [showRsi,    setShowRsi]    = useState(true)
  const [showTrend,  setShowTrend]  = useState(true)
  const [showMa200,  setShowMa200]  = useState(true)

  // Bot status state
  const [status, setStatus]       = useState<BotStatus>({ configured: false, state: 'unknown' })
  const [statusLoading, setStatusLoading] = useState(true)
  const [actionLoading, setActionLoading] = useState<'start' | 'stop' | 'restart' | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Load config values once
  useEffect(() => {
    fetchConfigs().then(() => {
      setDailyH(getConfigValue('bot_daily_report_hour', '8'))
      setDailyM(getConfigValue('bot_daily_report_minute', '0'))
      setAlphaH(getConfigValue('bot_alpha_check_hour', '9'))
      setAlphaM(getConfigValue('bot_alpha_check_minute', '0'))
      setFinvizH(getConfigValue('bot_finviz_hour', '7'))
      setFinvizM(getConfigValue('bot_finviz_minute', '0'))
      setTimeframe(getConfigValue('bot_timeframe', '1d'))
      setShowDayPct(getConfigValue('bot_report_show_day_pct', 'true') !== 'false')
      setShowRsi(getConfigValue('bot_report_show_rsi',    'true') !== 'false')
      setShowTrend(getConfigValue('bot_report_show_trend',  'true') !== 'false')
      setShowMa200(getConfigValue('bot_report_show_ma200',  'true') !== 'false')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Poll bot status
  const fetchStatus = useCallback(async () => {
    try {
      const res = await apiFetch('/api/bot/status')
      if (!res.ok) return
      const data: BotStatus = await res.json()
      setStatus(data)
    } catch {
      setStatus(prev => ({ ...prev, state: 'unknown' }))
    } finally {
      setStatusLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    pollRef.current = setInterval(fetchStatus, 6000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [fetchStatus])

  // Faster polling while transitioning
  useEffect(() => {
    const transitioning = status.state === 'starting' || status.state === 'stopping'
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = setInterval(fetchStatus, transitioning ? 2000 : 6000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [status.state, fetchStatus])

  const handleStart = async () => {
    setActionLoading('start')
    try {
      const res = await apiFetch('/api/bot/start', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setStatus(prev => ({ ...prev, state: 'starting' }))
      toast.success('Bot กำลังเริ่มทำงาน…')
    } catch (err: any) {
      toast.error(err.message || 'เริ่ม bot ไม่สำเร็จ')
    } finally {
      setActionLoading(null)
    }
  }

  const handleStop = async () => {
    setActionLoading('stop')
    try {
      const res = await apiFetch('/api/bot/stop', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setStatus(prev => ({ ...prev, state: 'stopping' }))
      toast.success('Bot กำลังหยุดทำงาน…')
    } catch (err: any) {
      toast.error(err.message || 'หยุด bot ไม่สำเร็จ')
    } finally {
      setActionLoading(null)
    }
  }

  const handleRestart = async () => {
    setActionLoading('restart')
    try {
      const res = await apiFetch('/api/bot/restart', { method: 'POST' })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error)
      setStatus(prev => ({ ...prev, state: 'starting' }))
      toast.success('Bot กำลัง restart…')
    } catch (err: any) {
      toast.error(err.message || 'restart bot ไม่สำเร็จ')
    } finally {
      setActionLoading(null)
    }
  }

  const saveSection = async (id: string, pairs: [string, string][]) => {
    setSaving(id)
    try {
      await Promise.all(pairs.map(([key, val]) =>
        updateConfig(key, String(val) || '0', `Bot setting: ${key}`)
      ))
      toast.success('บันทึกแล้ว — มีผลเมื่อ restart bot')
    } catch {
      toast.error('บันทึกไม่สำเร็จ')
    } finally {
      setSaving(null)
    }
  }

  const meta = STATE_META[status.state] ?? STATE_META.unknown
  const notTransitioning = !['starting', 'stopping', 'replacing', 'destroying'].includes(status.state)
  const canStart   = status.configured && ['stopped', 'created', 'unknown', 'destroyed'].includes(status.state) && notTransitioning && !actionLoading
  const canStop    = status.configured && ['started', 'unknown'].includes(status.state) && notTransitioning && !actionLoading
  const canRestart = status.configured && ['started', 'unknown'].includes(status.state) && notTransitioning && !actionLoading

  return (
    <div>
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 bg-brand-soft rounded-lg flex items-center justify-center">
          <Bot className="w-4 h-4 text-primary-600 dark:text-primary-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Bot Settings</h3>
      </div>

      {/* ── Status + Control card ── */}
      <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600 mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {statusLoading ? (
              <RefreshCw className="w-4 h-4 text-gray-400 animate-spin" />
            ) : status.configured ? (
              <span className="relative flex h-3 w-3">
                {meta.pulse && <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${meta.dot} opacity-75`} />}
                <span className={`relative inline-flex rounded-full h-3 w-3 ${meta.dot}`} />
              </span>
            ) : (
              <WifiOff className="w-4 h-4 text-gray-400" />
            )}
            <div>
              <div className={`text-sm font-semibold ${status.configured ? meta.text : 'text-gray-400'}`}>
                {statusLoading ? 'กำลังตรวจสอบ…' : status.configured ? meta.label : 'ไม่ได้ตั้งค่า FLY_API_TOKEN'}
              </div>
              {status.updated_at && !statusLoading && (
                <div className="text-[10px] text-gray-400 mt-0.5">
                  อัพเดทล่าสุด {new Date(status.updated_at).toLocaleTimeString('th-TH')}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={fetchStatus}
              disabled={statusLoading}
              className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-40"
              title="รีเฟรชสถานะ"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${statusLoading ? 'animate-spin' : ''}`} />
            </button>
            <Button size="sm"
              onClick={handleStart}
              disabled={!canStart}
            >
              {actionLoading === 'start'
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Play className="w-3.5 h-3.5 fill-white" />}
              Start
            </Button>
            <Button variant="secondary" size="sm"
              onClick={handleRestart}
              disabled={!canRestart}
            >
              {actionLoading === 'restart'
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <RotateCw className="w-3.5 h-3.5" />}
              Restart
            </Button>
            <Button variant="danger" size="sm"
              onClick={handleStop}
              disabled={!canStop}
            >
              {actionLoading === 'stop'
                ? <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                : <Square className="w-3.5 h-3.5 fill-white" />}
              Stop
            </Button>
          </div>
        </div>

        {!status.configured && !statusLoading && (
          <div className="mt-3 text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-lg px-3 py-2">
            เพิ่ม <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">FLY_API_TOKEN</code> และ{' '}
            <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">FLY_BOT_APP_NAME</code> ใน env vars ของ web server เพื่อเปิดใช้งาน
          </div>
        )}
        {status.configured && status.error && !statusLoading && (
          <div className="mt-3 text-xs text-loss bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2">
            Fly API error: {status.error}
          </div>
        )}
      </div>

      {/* Restart notice */}
      <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl p-3 mb-5">
        <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700 dark:text-amber-300">
          การตั้งค่าตารางเวลาด้านล่างจะมีผลเมื่อ <strong>restart bot</strong> ครั้งถัดไป
        </p>
      </div>

      <div className="space-y-4">
        {/* Telegram Link */}
        <TelegramLinkCard />

        {/* Daily Report */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Daily Report</span>
            <span className="text-xs text-gray-400">ส่งรายงานประจำวัน</span>
          </div>
          <div className="flex items-center gap-4">
            <TimePicker hour={dailyH} minute={dailyM} onHourChange={setDailyH} onMinuteChange={setDailyM} />
            <Button size="sm"
              onClick={() => saveSection('daily', [['bot_daily_report_hour', dailyH], ['bot_daily_report_minute', dailyM]])}
              disabled={saving === 'daily'}
             
            >
              <Save className="w-3.5 h-3.5" />
              {saving === 'daily' ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Alpha Check */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Alpha Picks Check</span>
            <span className="text-xs text-gray-400">ตรวจหุ้นใหม่ใน Alpha Picks</span>
          </div>
          <div className="flex items-center gap-4">
            <TimePicker hour={alphaH} minute={alphaM} onHourChange={setAlphaH} onMinuteChange={setAlphaM} />
            <Button size="sm"
              onClick={() => saveSection('alpha', [['bot_alpha_check_hour', alphaH], ['bot_alpha_check_minute', alphaM]])}
              disabled={saving === 'alpha'}
             
            >
              <Save className="w-3.5 h-3.5" />
              {saving === 'alpha' ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Finviz Bubbles */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Finviz Bubbles</span>
            <span className="text-xs text-gray-400">ส่ง S&P 500 Bubble Chart</span>
          </div>
          <div className="flex items-center gap-4">
            <TimePicker hour={finvizH} minute={finvizM} onHourChange={setFinvizH} onMinuteChange={setFinvizM} />
            <Button size="sm"
              onClick={() => saveSection('finviz', [['bot_finviz_hour', finvizH], ['bot_finviz_minute', finvizM]])}
              disabled={saving === 'finviz'}
             
            >
              <Save className="w-3.5 h-3.5" />
              {saving === 'finviz' ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>

        {/* Default Timeframe */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <RotateCcw className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Default Timeframe</span>
            <span className="text-xs text-gray-400">Timeframe เริ่มต้นเมื่อ bot เริ่มทำงาน</span>
          </div>
          <div className="flex items-center gap-4">
            <select
              value={timeframe}
              onChange={e => setTimeframe(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            >
              {TIMEFRAMES.map(tf => (
                <option key={tf.value} value={tf.value}>{tf.label} ({tf.value})</option>
              ))}
            </select>
            <Button size="sm"
              onClick={() => saveSection('tf', [['bot_timeframe', timeframe]])}
              disabled={saving === 'tf'}
             
            >
              <Save className="w-3.5 h-3.5" />
              {saving === 'tf' ? 'Saving…' : 'Save'}
            </Button>
          </div>
          <p className="text-xs text-gray-400 dark:text-gray-400 mt-2">
            เปลี่ยนขณะ bot ทำงานได้ด้วย{' '}
            <code className="bg-gray-200 dark:bg-gray-600 px-1 rounded">/tf</code> ใน Telegram
          </p>
        </div>
        {/* Report Template */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-xl p-4 border border-gray-200 dark:border-gray-600">
          <div className="flex items-center gap-2 mb-3">
            <FileText className="w-4 h-4 text-primary-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-white">Report Template</span>
            <span className="text-xs text-gray-400">เลือกข้อมูลที่แสดงใน /report</span>
          </div>
          <div className="space-y-2 mb-3">
            {([
              { key: 'day_pct', label: '% เปลี่ยนแปลงวันนี้',  state: showDayPct, set: setShowDayPct },
              { key: 'rsi',     label: 'RSI',                    state: showRsi,    set: setShowRsi    },
              { key: 'trend',   label: 'แนวโน้ม (ขาขึ้น/ขาลง)', state: showTrend,  set: setShowTrend  },
              { key: 'ma200',   label: 'เทียบ MA200 %',          state: showMa200,  set: setShowMa200  },
            ] as const).map(({ key, label, state, set }) => (
              <label key={key} className="flex items-center justify-between cursor-pointer group">
                <span className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-gray-900 dark:group-hover:text-white transition-colors">
                  {label}
                </span>
                <div
                  onClick={() => set(!state)}
                  className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${
                    state ? 'bg-gradient-to-r from-blue-500 to-purple-600' : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                    state ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </div>
              </label>
            ))}
          </div>
          <Button size="sm"
            onClick={() => saveSection('template', [
              ['bot_report_show_day_pct', String(showDayPct)],
              ['bot_report_show_rsi',     String(showRsi)],
              ['bot_report_show_trend',   String(showTrend)],
              ['bot_report_show_ma200',   String(showMa200)],
            ])}
            disabled={saving === 'template'}
           
          >
            <Save className="w-3.5 h-3.5" />
            {saving === 'template' ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  )
}

export default BotSettingsSection
